import { EmptyStateTable } from '@components'

import { Table } from '@shinetools/lumen-react'
import type { TableSortDirection } from '@shinetools/lumen-react'
import { ReactElement, ReactNode, useCallback, useMemo } from 'react'

import { LedgerEntryColumn } from '@features/balanceLedger/enums/ledgerEntryColumn'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { LEDGER_ENTRIES_TABLE_MIN_WIDTH } from '../../../../constants/ledgerEntriesColumnWidths'
import { SuppliersBalanceEmptyState } from '../../../../elements/SuppliersBalanceEmptyState/SuppliersBalanceEmptyState'
import type { ItemsPerPage } from '../../../../elements/TableFooter'
import { TableFooter } from '../../../../elements/TableFooter'
import { TableHeader } from '../../../../elements/TableHeader'
import { useLedgerEntriesTableColumns } from '../../../../hooks/useLedgerEntriesTableColumns'
import type { LedgerEntriesSortState } from '../../../../types/LedgerEntriesSortState'
import type { LedgerEntry } from '../../../../types/LedgerEntry'
import { LedgerEntriesToolbar } from '../LedgerEntriesToolbar'
import { LedgerEntryRow } from './elements/LedgerEntryRow'
import { OpeningBalanceGroupHeader } from './elements/OpeningBalanceGroupHeader'
import { TotalBalanceRow } from './elements/TotalBalanceRow'
import * as Styled from './styles'

const isLedgerEntryColumn = (columnId: string): columnId is LedgerEntryColumn =>
  (Object.values(LedgerEntryColumn) as string[]).includes(columnId)

interface LedgerEntriesPagination {
  currentPage: number
  itemsPerPage: ItemsPerPage
  totalPageItems: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: ItemsPerPage) => void
}

interface LedgerEntriesTableProps {
  data: LedgerEntry[]
  totalBalance: number
  openingBalance?: number
  pagination: LedgerEntriesPagination
  hasActiveSearch: boolean
  isLoading?: boolean
  ledgerSearchQuery?: string
  onSearchChange: (ledgerSearchQuery?: string) => void
  onSort: (columnId: LedgerEntryColumn, isDesc: boolean) => void
  queryKey: string
  sortState?: LedgerEntriesSortState
  emptyRowInfo?: ReactNode
}

export const LedgerEntriesTable = ({
  data,
  totalBalance,
  openingBalance,
  pagination,
  hasActiveSearch,
  isLoading,
  ledgerSearchQuery,
  onSearchChange,
  onSort,
  queryKey,
  sortState,
  emptyRowInfo,
}: LedgerEntriesTableProps): ReactElement => {
  const { currentPage, itemsPerPage, totalPageItems, onPageChange, onItemsPerPageChange } = pagination
  const { organization } = useUserOrganization()
  const columns = useLedgerEntriesTableColumns(organization?.baseCurrencyId)

  const handleSort = useCallback(
    (columnId: string) => {
      if (!isLedgerEntryColumn(columnId)) {
        return
      }
      const isCurrentColumn = sortState?.columnId === columnId
      onSort(columnId, isCurrentColumn ? !sortState.isDesc : true)
    },
    [onSort, sortState],
  )

  const getSortDirection = useCallback(
    (columnId: LedgerEntryColumn): TableSortDirection => {
      if (sortState?.columnId !== columnId) {
        return false
      }
      return sortState.isDesc ? 'descending' : 'ascending'
    },
    [sortState],
  )

  const openingBalanceEntries = useMemo(() => data.filter((entry) => entry.isOpeningBalance), [data])
  const regularEntries = useMemo(() => data.filter((entry) => !entry.isOpeningBalance), [data])
  const hasOpeningBalanceEntries = openingBalanceEntries.length > 0
  // Carried-in rows are already in the statement on a fiscal-year-start period. On any other
  // period the amount stands alone, and a search hides that summary because it is not an entry.
  const hasOpeningBalanceSection = hasOpeningBalanceEntries || (!hasActiveSearch && Boolean(openingBalance))
  const hasData = data.length > 0 || (hasOpeningBalanceSection && !hasActiveSearch)
  const showEmptyStateTable = !hasData && !isLoading && !hasOpeningBalanceSection
  const emptyState = emptyRowInfo ?? <SuppliersBalanceEmptyState />

  const renderRow = (entry: LedgerEntry): ReactElement => (
    <LedgerEntryRow key={entry.id} columns={columns} entry={entry} />
  )

  const renderTableBody = (): ReactElement => (
    <Table.Body>
      {hasOpeningBalanceSection && (
        <OpeningBalanceGroupHeader
          columns={columns}
          count={openingBalanceEntries.length}
          totalBalance={openingBalance ?? 0}
        />
      )}
      {openingBalanceEntries.map(renderRow)}
      {regularEntries.map(renderRow)}
      {!hasData && (
        <Table.Row>
          <Table.Cell colSpan={columns.length}>{emptyState}</Table.Cell>
        </Table.Row>
      )}
      {hasData && <TotalBalanceRow columns={columns} isSticky totalBalance={totalBalance} />}
    </Table.Body>
  )

  const tableHeader = <TableHeader columns={columns} getSortDirection={getSortDirection} onSort={handleSort} />
  const scrollResetKey = [
    queryKey,
    currentPage,
    itemsPerPage,
    sortState?.columnId ?? '',
    sortState?.isDesc ?? '',
    ledgerSearchQuery ?? '',
  ].join('-')

  return (
    <Styled.LedgerEntriesTableWrapper>
      <Styled.LedgerEntriesToolbarSlot>
        <LedgerEntriesToolbar ledgerSearchQuery={ledgerSearchQuery} onSearchChange={onSearchChange} />
      </Styled.LedgerEntriesToolbarSlot>
      <Styled.LedgerEntriesTableScrollArea>
        {showEmptyStateTable ? (
          <EmptyStateTable header={tableHeader} minWidth={LEDGER_ENTRIES_TABLE_MIN_WIDTH}>
            {emptyState}
          </EmptyStateTable>
        ) : (
          <Table compact>
            <Table.Content
              maxHeight="none"
              scrollable
              scrollResetKey={scrollResetKey}
              style={{ minWidth: LEDGER_ENTRIES_TABLE_MIN_WIDTH }}
            >
              {tableHeader}
              {isLoading ? <Table.Skeleton columns={columns.length} /> : renderTableBody()}
            </Table.Content>
            {hasData && (
              <TableFooter
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={onItemsPerPageChange}
                onPageChange={onPageChange}
                totalPageItems={totalPageItems}
              />
            )}
          </Table>
        )}
      </Styled.LedgerEntriesTableScrollArea>
    </Styled.LedgerEntriesTableWrapper>
  )
}
